import { ICategory } from 'src/components/side-menu/sub-elements/Category'
import useBasicLawsMenuStructure from 'src/components/side-menu/side-menu-structure/basic-laws'
import useGlossaryMenuStructure from 'src/components/side-menu/side-menu-structure/glossary'
import useGuidelinesMenuStructure from 'src/components/side-menu/side-menu-structure/guidelines'
import useLawsChangesMenuStructure from 'src/components/side-menu/side-menu-structure/law-changes'
import lawsStore from 'src/components/laws/laws-store'
import { ISubCategory } from 'src/components/side-menu/sub-elements/SubCategory'

const slugOfSeason20and21 = '2020-21'
const slugOfSeason21and22 = '2021-22'
const slugOfSeason24and25 = '2024-25'
const slugOfSeason26and27 = '2026-27'

const useMenuStructure = (): ICategory[] => {
  const basicLawsStructure = useBasicLawsMenuStructure()
  const glossaryStructure = useGlossaryMenuStructure()
  const guidelinesStructure = useGuidelinesMenuStructure()
  const lawChangesMenuStructure = useLawsChangesMenuStructure()
  const selectedSeason = lawsStore((state) => state.selectedSeason)

  const isSeason20and21 = () => selectedSeason.slug === slugOfSeason20and21
  const isSeason21and22 = () => selectedSeason.slug === slugOfSeason21and22

  const getTemporaryLaws = (): Array<ICategory> => {
    if (isSeason21and22()) {
      return [
        {
          name: 'Temporary Laws',
          id: 'temporary-laws',
          subcategories: [
            {
              name: 'Additional Substitutes',
              id: 'additional-substitutes',
              link: `/laws/${selectedSeason.slug}/temporary-amendment-covid/`,
            },
          ],
        },
      ]
    }

    return []
  }

  const getFifaQualityProgramme = (): Array<ISubCategory> => {
    if (isSeason20and21()) {
      return []
    }

    return [
      {
        name: 'FIFA Quality Programme',
        id: 'fifa-quality-program',
        link: `/laws/${selectedSeason.slug}/fifa-quality-programme/`,
      },
    ]
  }

  const getAboutTheLawsOrIntroduction = (): ISubCategory => {
    if (isSeason20and21()) {
      return {
        name: 'Introduction',
        id: 'about-the-laws',
        link: `/laws/${selectedSeason.slug}/about-the-laws/`,
      }
    }

    return {
      name: 'About the laws',
      id: 'about-the-laws',
      link: `/laws/${selectedSeason.slug}/about-the-laws/`,
    }
  }

  const getNotesAndModifications = (): ISubCategory => {
    if (isSeason21and22()) {
      return {
        name: 'Notes and modifications',
        id: 'notes-and-modifications',
        link: `/laws/${selectedSeason.slug}/notes-and-modifications/`,
      }
    }

    const getOnlyTheCaptain = (): ISubCategory[] => 
      selectedSeason.slug > slugOfSeason24and25 
        ? [{
          name: '‘Only the captain’ guidelines',
          id: 'only-the-captain',
          link: `/laws/${selectedSeason.slug}/only-the-captain/`,
        }]
        : []

    const get2026protocols = (): ISubCategory[] => 
      selectedSeason.slug >= slugOfSeason26and27 
        ? [
          {
            name: 'Time-limited substitution protocol',
            id: 'time-limited-substitution-protocol',
            link: `/laws/${selectedSeason.slug}/time-limited-substitution-protocol/`,
          },
          {
            name: 'Off-field treatment and assessment protocol',
            id: 'off-field-treatment-and-assessment-protocol',
            link: `/laws/${selectedSeason.slug}/off-field-treatment-and-assessment-protocol/`,
          },
          {
            name: 'Throw-in and goal-kick countdown protocol',
            id: 'throw-in-and-goal-kick-countdown-protocol',
            link: `/laws/${selectedSeason.slug}/throw-in-and-goal-kick-countdown-protocol/`,
          },
        ]
        : []

    return {
      name: 'Notes and modifications',
      id: 'notes-and-modifications',
      elements: [
        {
          name: 'Notes on the Laws',
          id: 'notes-on-the-laws',
          link: `/laws/${selectedSeason.slug}/notes-on-the-laws/`,
        },
        {
          name: 'General Modifications',
          id: 'general-modifications',
          link: `/laws/${selectedSeason.slug}/general-modifications/`,
        },
        ...get2026protocols(),
        ...getOnlyTheCaptain(),
        {
          name: 'Guidelines for Temporary Dismissals',
          id: 'guidelines-for-temporary-dismissals',
          link: `/laws/${selectedSeason.slug}/guidelines-for-temporary-dismissals/`,
        },
        {
          name: 'Guidelines for Return Substitutes',
          id: 'guidelines-for-return-substitutes',
          link: `/laws/${selectedSeason.slug}/guidelines-for-return-substitutes/`,
        },
        {
          name: 'Additional permanent concussion substitutions protocol',
          id: 'additional-permanent-concussion-substitutions-protocol',
          link: `/laws/${selectedSeason.slug}/additional-permanent-concussion-substitutions-protocol/`,
        },
      ],
    }
  }

  const getEditorialChanges = (): ISubCategory[] => {
    if (!isSeason20and21()) {
      return []
    }

    return [
      {
        name: 'Editorial changes',
        id: 'editorial-changes',
        link: `/laws/${selectedSeason.slug}/editorial-changes/`,
      },
    ]
  }

  return [
    {
      name: 'Laws of the game',
      isSeasonsSwitch: true,
      id: 'laws-of-the-game',
      subcategories: [
        getAboutTheLawsOrIntroduction(),
        getNotesAndModifications(),
        basicLawsStructure,
        {
          name: 'VAR protocol',
          id: 'var-protocol',
          link: `/laws/${selectedSeason.slug}/video-assistant-referee-var-protocol/`,
        },
        ...getFifaQualityProgramme(),
        glossaryStructure,
        guidelinesStructure,
        ...getEditorialChanges(),
        {
          name: 'PDF Downloads',
          id: 'laws-of-the-game-documents',
          link: '/laws-of-the-game-documents/',
        },
        {
          name: 'Audio Files',
          id: 'laws-of-the-game-audio',
          link: '/laws-of-the-game-audio/',
        },
      ],
    },
    lawChangesMenuStructure,
    ...getTemporaryLaws(),
    {
      name: 'Trials',
      id: 'trials',
      subcategories: [
        {
          name: 'Goalkeeper injury',
          id: 'goalkeeper-injury',
          link: `/trials/goalkeeper-injury/`,
        },
        {
          name: 'VAR - attacking team offence before the ball is in play',
          id: 'VAR-attacking-team-offence-before-the-ball-is-in-play',
          link: `/trials/VAR-attacking-team-offence-before-the-ball-is-in-play/`,
        },
        {
          name: 'COOLING-OFF PERIODS',
          id: 'cooling-off-periods',
          link: `/trials/cooling-off-periods/`,
        },
        {
          name: 'Deliberate heading',
          id: 'deliberate-heading ',
          link: `/trials/deliberate-heading-trial/`,
        },
      ],
    },
    {
      name: 'The IFAB',
      id: 'the-ifab',
      subcategories: [
        {
          name: 'Background',
          id: 'background',
          link: `/background/`,
        },
        {
          name: 'Organisation',
          id: 'organisation',
          link: `/organisation/`,
        },
        {
          name: 'Work process',
          id: 'work-process',
          link: `/work-process/`,
        },
        {
          name: 'Calendar',
          id: 'calendar',
          link: `/calendar/`,
        },
      ],
    },
    {
      name: 'Documents',
      id: 'documents-category',
      link: '/documents/',
    },
    {
      name: 'News',
      id: 'news',
      link: '/news/',
    },
  ]
}

export default useMenuStructure
